import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useArchiveContactMutation,
  useDeleteSiteMutation,
  useGetSiteFormsQuery,
  useGetSiteLeadsQuery,
  useGetSiteRecentLeadCountQuery,
  useMarkSiteLeadAsSpamMutation,
  useGetSitePagesQuery,
  useGetSiteQuery,
  usePublishSiteMutation,
  useUnpublishSiteMutation,
  useGenerateSitePreviewTokenMutation,
  useUpdateSiteFormMutation,
  useUpdateSiteMutation,
  useUpdateSitePageMutation,
} from "@/store/rest_api";
import { ContentBlock, FormSubmission, Page, Site, SiteForm } from "@/model/interface/Site";
import { ADD_ONS_PATH, sitePublicUrl } from "@/components/pages/sites/SitesPage";
import { useAddOn } from "@/hooks/useAddOn";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Archive, ArrowLeft, Ban, ChevronDown, ExternalLink, LockIcon, MoreVertical, Plus, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";
import { AddOnType } from "@/enum/AddOnType";

const BLOCK_LABELS: Record<string, string> = {
  hero: "Hero",
  trustBar: "Trust Bar",
  howItWorks: "How It Works",
  leadForm: "Call To Action",
  testimonials: "Testimonials",
  faq: "FAQ",
  footer: "Footer",
};

const KEY_LABELS: Record<string, string> = {
  eyebrow: "Eyebrow",
  headline: "Headline",
  subheadline: "Subheadline",
  ctaLabel: "Button Label",
  formTitle: "Form Title",
  formSubtitle: "Form Subtitle",
  title: "Title",
  description: "Description",
  text: "Text",
  body: "Body",
  quote: "Quote",
  author: "Author",
  q: "Question",
  a: "Answer",
  value: "Value",
  label: "Label",
  items: "Items",
  steps: "Steps",
  points: "Points",
  stats: "Stats",
};

const LONG_KEYS = new Set(["subheadline", "description", "text", "body", "quote", "a"]);

function humanizeKey(key: string): string {
  return KEY_LABELS[key] ?? key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());
}

function blockLabel(block: ContentBlock): string {
  return BLOCK_LABELS[block.type ?? ""] ?? (block.type ?? "Section");
}

function StringField({ fieldKey, value, onChange }: { fieldKey: string; value: string; onChange: (value: string) => void }) {
  return LONG_KEYS.has(fieldKey) ? (
    <Textarea value={value} onChange={(event) => onChange(event.target.value)} />
  ) : (
    <Input value={value} onChange={(event) => onChange(event.target.value)} />
  );
}

function PropField({
  fieldKey,
  value,
  onChange,
}: {
  fieldKey: string;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  if (typeof value === "string") {
    return (
      <div className="space-y-1.5">
        <Label>{humanizeKey(fieldKey)}</Label>
        <StringField fieldKey={fieldKey} value={value} onChange={onChange} />
      </div>
    );
  }

  if (Array.isArray(value)) {
    const isStringList = value.every((item) => typeof item === "string");
    return (
      <div className="space-y-2">
        <Label>{humanizeKey(fieldKey)}</Label>
        {value.map((item, index) => (
          <div key={index} className="rounded-md border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {humanizeKey(fieldKey)} {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {isStringList ? (
              <StringField
                fieldKey={fieldKey}
                value={item as string}
                onChange={(next) => onChange(value.map((current, i) => (i === index ? next : current)))}
              />
            ) : (
              <div className="space-y-2">
                {Object.keys(item as Record<string, unknown>).map((subKey) => (
                  <div key={subKey} className="space-y-1">
                    <Label className="text-xs">{humanizeKey(subKey)}</Label>
                    <StringField
                      fieldKey={subKey}
                      value={String((item as Record<string, unknown>)[subKey] ?? "")}
                      onChange={(next) =>
                        onChange(
                          value.map((current, i) =>
                            i === index ? { ...(current as Record<string, unknown>), [subKey]: next } : current,
                          ),
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!isStringList && value.length === 0}
          onClick={() => {
            const blank = isStringList
              ? ""
              : Object.fromEntries(Object.keys((value[0] as Record<string, unknown>) ?? {}).map((key) => [key, ""]));
            onChange([...value, blank]);
          }}
        >
          <Plus className="h-4 w-4 mr-1" /> Add {humanizeKey(fieldKey).toLowerCase()}
        </Button>
      </div>
    );
  }

  return null;
}

function BlockCard({
  block,
  index,
  onToggleEnabled,
  onSetProp,
}: {
  block: ContentBlock;
  index: number;
  onToggleEnabled: (index: number, enabled: boolean) => void;
  onSetProp: (index: number, key: string, value: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const props = (block.props ?? {}) as Record<string, unknown>;
  const hasContent = Object.keys(props).length > 0;

  return (
    <Card className={block.enabled ? "" : "opacity-60"}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <button
          type="button"
          className="flex items-center gap-2 text-left flex-1"
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
          <CardTitle className="text-base">{blockLabel(block)}</CardTitle>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{block.enabled ? "Visible" : "Hidden"}</span>
          <Switch checked={block.enabled} onCheckedChange={(checked) => onToggleEnabled(index, checked)} />
        </div>
      </CardHeader>
      {open && hasContent && (
        <CardContent className="space-y-4">
          {Object.entries(props).map(([key, value]) => (
            <PropField key={key} fieldKey={key} value={value} onChange={(next) => onSetProp(index, key, next)} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function ContentTab({ siteId, page }: { siteId: string; page: Page }) {
  const [blocks, setBlocks] = useState<ContentBlock[]>(page.contentBlocks ?? []);
  const [updatePage, { isLoading }] = useUpdateSitePageMutation();

  useEffect(() => {
    setBlocks(page.contentBlocks ?? []);
  }, [page.id]);

  const setEnabled = (index: number, enabled: boolean) =>
    setBlocks((prev) => prev.map((block, i) => (i === index ? { ...block, enabled } : block)));

  const setProp = (index: number, key: string, value: unknown) =>
    setBlocks((prev) =>
      prev.map((block, i) => (i === index ? { ...block, props: { ...(block.props ?? {}), [key]: value } } : block)),
    );

  const save = async () => {
    try {
      await updatePage({ siteId, pageId: page.id, data: { contentBlocks: blocks } }).unwrap();
      toast.success("Content saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save content"));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Toggle sections on or off and edit everything in them.</p>
      {blocks.map((block, index) => (
        <BlockCard
          key={block.id ?? index}
          block={block}
          index={index}
          onToggleEnabled={setEnabled}
          onSetProp={setProp}
        />
      ))}
      <div className="flex justify-end">
        <Button onClick={save} disabled={isLoading}>
          Save content
        </Button>
      </div>
    </div>
  );
}

function FormsTab({ siteId, forms }: { siteId: string; forms: SiteForm[] }) {
  const [updateForm, { isLoading }] = useUpdateSiteFormMutation();
  const form = forms[0];
  const [consentText, setConsentText] = useState(form?.consentText ?? "");
  const [captchaEnabled, setCaptchaEnabled] = useState(form?.captchaEnabled ?? true);

  useEffect(() => {
    setConsentText(form?.consentText ?? "");
    setCaptchaEnabled(form?.captchaEnabled ?? true);
  }, [form?.id]);

  if (!form) {
    return <p className="text-muted-foreground">This site has no form.</p>;
  }

  const save = async () => {
    try {
      await updateForm({ siteId, formId: form.id, data: { consentText, captchaEnabled } }).unwrap();
      toast.success("Form saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save form"));
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{form.name}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Fields</Label>
            <div className="flex flex-wrap gap-2">
              {(form.fields ?? []).map((field) => (
                <Badge key={field.name} variant="secondary">
                  {field.label}
                  {field.required ? " *" : ""}
                </Badge>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Consent text (TCPA)</Label>
            <Textarea value={consentText} onChange={(event) => setConsentText(event.target.value)} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Spam protection</Label>
              <p className="text-sm text-muted-foreground">Require a Cloudflare Turnstile challenge.</p>
            </div>
            <Switch checked={captchaEnabled} onCheckedChange={setCaptchaEnabled} />
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={isLoading}>
              Save form
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsTab({ site }: { site: Site }) {
  const [updateSite, { isLoading }] = useUpdateSiteMutation();
  const [name, setName] = useState(site.name);
  const [slug, setSlug] = useState(site.slug);
  const [theme, setTheme] = useState(site.theme ?? {});
  const [tracking, setTracking] = useState(site.trackingIds ?? {});

  useEffect(() => {
    setName(site.name);
    setSlug(site.slug);
    setTheme(site.theme ?? {});
    setTracking(site.trackingIds ?? {});
  }, [site.id]);

  const save = async () => {
    try {
      await updateSite({ id: site.id, data: { name, slug, theme, trackingIds: tracking } }).unwrap();
      toast.success("Settings saved");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to save settings"));
    }
  };

  const colorField = (label: string, key: "primaryColor" | "secondaryColor" | "accentColor") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={(theme[key] as string) ?? "#000000"}
          onChange={(event) => setTheme({ ...theme, [key]: event.target.value })}
          className="h-9 w-12 rounded border"
        />
        <Input value={(theme[key] as string) ?? ""} onChange={(event) => setTheme({ ...theme, [key]: event.target.value })} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Subdomain</Label>
            <div className="flex items-center gap-2">
              <Input value={slug} onChange={(event) => setSlug(event.target.value)} className="max-w-[200px]" />
              <span className="text-sm text-muted-foreground">.onretreevo.com</span>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Business name</Label>
            <Input
              value={(theme.businessName as string) ?? ""}
              onChange={(event) => setTheme({ ...theme, businessName: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={(theme.phone as string) ?? ""}
              onChange={(event) => setTheme({ ...theme, phone: event.target.value })}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {colorField("Primary", "primaryColor")}
            {colorField("Secondary", "secondaryColor")}
            {colorField("Accent", "accentColor")}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tracking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Google Analytics 4 ID</Label>
            <Input
              value={tracking.ga4MeasurementId ?? ""}
              placeholder="G-XXXXXXXXXX"
              onChange={(event) => setTracking({ ...tracking, ga4MeasurementId: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Meta Pixel ID</Label>
            <Input
              value={tracking.metaPixelId ?? ""}
              onChange={(event) => setTracking({ ...tracking, metaPixelId: event.target.value })}
            />
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button onClick={save} disabled={isLoading}>
          Save settings
        </Button>
      </div>
    </div>
  );
}

const LEADS_PAGE_SIZE = 25;

function formatLeadDate(value?: number): string {
  if (!value) {
    return "";
  }
  const date = new Date(value < 1e12 ? value * 1000 : value);
  return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}

function leadValue(payload: Record<string, unknown>, ...keys: string[]): string {
  const values = keys
    .map((key) => payload[key])
    .filter((value): value is string | number => typeof value === "string" || typeof value === "number")
    .map((value) => String(value).trim());
  return values.find((value) => value !== "") ?? "";
}

function LeadsTab({ siteId, slug }: { siteId: string; slug: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [leadToArchive, setLeadToArchive] = useState<FormSubmission | null>(null);
  const { data, isLoading } = useGetSiteLeadsQuery({ siteId, page, size: LEADS_PAGE_SIZE });
  const { data: recentCount } = useGetSiteRecentLeadCountQuery(siteId);
  const [markSiteLeadAsSpam] = useMarkSiteLeadAsSpamMutation();
  const [archiveContact] = useArchiveContactMutation();

  const openLead = (lead: FormSubmission) => {
    if (!lead.contactId) {
      return;
    }
    navigate(`/leads/${lead.contactId}`);
  };

  const markAsSpam = async (lead: FormSubmission) => {
    try {
      await markSiteLeadAsSpam({ siteId, submissionId: lead.id }).unwrap();
      toast.success("Marked as spam");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to mark as spam"));
    }
  };

  const archiveLead = async () => {
    if (!leadToArchive?.contactId) {
      return;
    }
    try {
      await archiveContact(leadToArchive.contactId).unwrap();
      toast.success("Lead archived");
      setLeadToArchive(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to archive lead"));
    }
  };

  useEffect(() => {
    if (data && page > 0 && page >= data.totalPages) {
      setPage(Math.max(data.totalPages - 1, 0));
    }
  }, [data, page]);

  if (isLoading) {
    return <p className="text-muted-foreground">Loading...</p>;
  }
  const leads = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:max-w-md">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total leads</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{data?.totalElements ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Last 7 days</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{recentCount ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      {leads.length === 0 ? (
        <p className="text-muted-foreground">No leads captured yet. New submissions show up here.</p>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Submissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="p-3 font-medium">Name</th>
                    <th className="p-3 font-medium">Phone</th>
                    <th className="p-3 font-medium">Email</th>
                    <th className="p-3 font-medium">Property</th>
                    <th className="p-3 font-medium">When</th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/50"
                      onClick={() => openLead(lead)}
                    >
                      <td className="p-3">{leadValue(lead.payload, "fullName", "name")}</td>
                      <td className="p-3">{leadValue(lead.payload, "phone", "phoneNumber")}</td>
                      <td className="p-3">{leadValue(lead.payload, "email")}</td>
                      <td className="p-3">{leadValue(lead.payload, "propertyAddress", "address")}</td>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">{formatLeadDate(lead.createdAt)}</td>
                      <td className="p-3 text-right" onClick={(event) => event.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" title="Options">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => markAsSpam(lead)}>
                              <Ban className="mr-2 h-4 w-4" />
                              Mark as spam
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setLeadToArchive(lead)} className="text-destructive">
                              <Archive className="mr-2 h-4 w-4" />
                              Archive lead
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {data.page + 1} of {data.totalPages}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!leadToArchive} onOpenChange={() => setLeadToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive lead</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">
                {(leadToArchive && leadValue(leadToArchive.payload, "fullName", "name")) || "This lead"}
              </span>{" "}
              leaves your leads, lists and inbox. Calls, texts, deals and tasks are kept, and the lead comes back on its own if they text, call or fill in the form again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={archiveLead} className="bg-destructive">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="text-sm text-muted-foreground">
        Every lead also lands in your{" "}
        <button className="text-primary underline" onClick={() => navigate("/leads")}>
          Leads
        </button>{" "}
        as a Contact + Deal, tagged <Badge variant="secondary">Website: {slug}</Badge>, with speed-to-lead automations.
      </div>
    </div>
  );
}

export function SiteDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: site, isLoading } = useGetSiteQuery(id);
  const { data: pages } = useGetSitePagesQuery(id);
  const { data: forms } = useGetSiteFormsQuery(id);
  const [publishSite, { isLoading: publishing }] = usePublishSiteMutation();
  const [unpublishSite, { isLoading: unpublishing }] = useUnpublishSiteMutation();
  const [deleteSite] = useDeleteSiteMutation();
  const [generatePreviewToken, { isLoading: previewing }] = useGenerateSitePreviewTokenMutation();
  const hasAddOn = useAddOn(AddOnType.WEBSITE_BUILDER);
  const [publishPromptOpen, setPublishPromptOpen] = useState(false);

  if (isLoading || !site) {
    return <div className="container p-4">Loading...</div>;
  }

  const published = site.status === "PUBLISHED";
  const homePage = pages?.find((page) => page.path === "/") ?? pages?.[0];

  const handlePreview = async () => {
    if (published) {
      window.open(sitePublicUrl(site.slug), "_blank");
      return;
    }
    try {
      const updated = await generatePreviewToken(site.id).unwrap();
      window.open(`${sitePublicUrl(updated.slug)}?preview=${updated.previewToken}`, "_blank");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to open preview"));
    }
  };

  const togglePublish = async () => {
    if (!published && !hasAddOn) {
      setPublishPromptOpen(true);
      return;
    }
    try {
      if (published) {
        await unpublishSite(site.id).unwrap();
        toast.success("Website unpublished");
      } else {
        await publishSite(site.id).unwrap();
        toast.success("Website published");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update website"));
    }
  };

  const remove = async () => {
    try {
      await deleteSite(site.id).unwrap();
      toast.success("Website deleted");
      navigate("/sites");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete website"));
    }
  };

  return (
    <div className="container p-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/sites")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{site.name}</h1>
              <Badge variant={published ? "default" : "secondary"}>{published ? "Published" : "Draft"}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{site.slug}.onretreevo.com</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={previewing}>
            <ExternalLink className="h-4 w-4 mr-2" /> Preview
          </Button>
          <Button onClick={togglePublish} disabled={publishing || unpublishing}>
            {published ? "Unpublish" : "Publish"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">Delete</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this website?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the site and its pages. Leads already captured are kept.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="form">Form</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="leads">Leads</TabsTrigger>
        </TabsList>
        <TabsContent value="content" className="mt-4">
          {homePage ? <ContentTab siteId={site.id} page={homePage} /> : <p>No page found.</p>}
        </TabsContent>
        <TabsContent value="form" className="mt-4">
          <FormsTab siteId={site.id} forms={forms ?? []} />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <SettingsTab site={site} />
        </TabsContent>
        <TabsContent value="leads" className="mt-4">
          <LeadsTab siteId={site.id} slug={site.slug} />
        </TabsContent>
      </Tabs>

      <Dialog open={publishPromptOpen} onOpenChange={setPublishPromptOpen}>
        <DialogContent>
          <DialogHeader>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 mb-2">
              <LockIcon className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle>Publishing requires the Website Builder add-on</DialogTitle>
            <DialogDescription>
              Your site is ready. Add Website Builder ($49/mo per published site) to take it live on {site.slug}
              .onretreevo.com.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishPromptOpen(false)}>
              Not now
            </Button>
            <Button onClick={() => navigate(ADD_ONS_PATH)}>Get Website Builder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
